import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAtomValue } from "jotai";
import { ROUTES } from "@routing/routes";
import { Icon, Spinner, ErrorState } from "@common/index";
import { useOrderApi } from "@/hooks/apiHooks/useOrderApi";
import { useLocationApi } from "@/hooks/apiHooks/useLocationApi";
import { useMountRead } from "@/hooks/useMountRead";
import { locationsAtom } from "@/store/locationAtoms";
import { asApiFailure } from "@utils/apiFailure";
import { currencyFormatter } from "@utils/formatter";
import type { LocationResponse } from "@models/location";
import {
    OrderPaymentStatusType,
    PENDING_SETTLEMENT_NOTICE,
    paymentFailureNote,
    type GetMeOrderResponse,
} from "@models/order";
import { CheckoutSteps } from "../Checkout/CheckoutSteps";
import { ShippingCard } from "../Checkout/ShippingCard";
import { ConfirmationItemRow } from "./ConfirmationItemRow";
import { InvoiceDownload } from "@components/Invoice/InvoiceDownload";
import { InvoiceVoidedBadge } from "@components/Invoice/InvoiceVoidedBadge";

const formatOrderDate = (iso: string): string =>
    new Date(iso).toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });

export const OrderConfirmation = () => {
    const { orderId = "" } = useParams();
    const { getMeOrder } = useOrderApi();
    const { getLocations } = useLocationApi();
    const locations = useAtomValue(locationsAtom);

    const [order, setOrder] = useState<GetMeOrderResponse | null>(null);

    // Only the LATEST read may store its order (#407). The route keeps this
    // page mounted when `:orderId` changes, and a previous order's read
    // answering last must not replace the route's own. useMountRead hands the
    // read `isCurrent`, which turns false once the hook issues a newer read;
    // and since the page shows an order only once the route's read has loaded,
    // the order on screen is always the route's.
    //
    // A 404 is the API saying there is no such order (PR 373 review 5): the
    // read answered, with no order, so the page says "Order not found" — not
    // a failure to retry. getMeOrder skips the toast for exactly that status.
    // Stored as null, or a previous order would stand in for it.
    const readOrder = useCallback(async (isCurrent: () => boolean) => {
        if (!orderId) return;
        let result: GetMeOrderResponse | null;
        try {
            result = await getMeOrder(orderId);
        } catch (error: unknown) {
            if (asApiFailure(error)?.status !== 404) throw error;
            result = null;
        }
        if (isCurrent()) setOrder(result);
    }, [orderId, getMeOrder]);

    // The status of the read THIS mount issued for THIS order (#407), never an
    // app-scoped error atom. Every read starts at "loading", a Retry included,
    // so a retry in flight shows the spinner and never "Order not found".
    const { readStatus, reload } = useMountRead(readOrder);

    // Populate the locations atom so the shipping address can be shown
    // (GetMeOrder returns only the location name).
    useEffect(() => {
        getLocations().catch(() => {
            /* notification layer surfaces errors */
        });
    }, [getLocations]);

    const products = useMemo(() => order?.products ?? [], [order]);

    if (readStatus === "loading") {
        return (
            <div className="flex justify-center items-center min-h-[50vh]">
                <Spinner size="lg" />
            </div>
        );
    }

    if (readStatus === "failed") {
        return (
            <div className="bg-base-200 text-neutral min-h-[60vh]">
                <div className="max-w-2xl mx-auto px-4 py-8">
                    <ErrorState onRetry={reload} />
                </div>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="bg-base-200 text-neutral min-h-[60vh]">
                <div className="max-w-2xl mx-auto px-4 py-8 text-center">
                    <h1 className="text-2xl font-bold">Order not found</h1>
                    <Link
                        to={ROUTES.BROWSE}
                        className="btn btn-warning mt-6 text-neutral font-bold"
                    >
                        Continue Shopping
                    </Link>
                </div>
            </div>
        );
    }

    const itemCount = products.reduce(
        (total, product) => total + product.quantity,
        0,
    );
    // Server-persisted breakdown (#176/#316): total already includes shipping, so never
    // re-add client-side. Legacy payloads (API deployed before the breakdown landed) lack
    // subtotal/shippingCost — fall back to total-only rendering then.
    const shippingAndHandling = order.shippingCost ?? 0;
    // #297: the persisted card fee, already inside `total` — never re-add it.
    // 0/null/missing (ACH, unpaid, pre-#297 payloads) renders no row.
    const surchargeAmount = order.surchargeAmount ?? 0;
    const orderTotal = order.total;
    // The legacy fallback has to BACK OUT the components `total` already contains, not just
    // echo it: with a fee row rendering beside it, "subtotal = total" printed a receipt whose
    // lines visibly didn't sum (subtotal $51.95 + fee $1.46 = total $51.95). Every term here
    // is inside `total` by contract, so the remainder is the real subtotal.
    const subtotal =
        order.subtotal ?? orderTotal - shippingAndHandling - surchargeAmount;
    // #445 / audit C-11: the order card's note, with the same precedence.
    const failureNote = paymentFailureNote(order);

    const shippingLocation =
        locations.find((location) => location.id === order.locationId) ??
        ({
            id: order.locationId,
            name: order.locationName,
            streetAddress: "",
            city: "",
            state: "",
            zipCode: "",
            country: "",
            isActive: true,
            createdAt: "",
            updatedAt: "",
            createdBy: "",
            updatedBy: "",
            roles: [],
        } satisfies LocationResponse);

    return (
        <div className="bg-base-200 text-neutral min-h-[60vh]">
            <div className="max-w-2xl mx-auto px-4 py-4 md:py-6 text-left flex flex-col gap-4">
                <div className="bg-base-100 rounded-lg p-4 md:p-5">
                    <CheckoutSteps current={3} />
                </div>

                <div className="bg-base-100 rounded-lg p-4 md:p-5 text-center">
                    <h1 className="text-xl md:text-2xl font-bold text-neutral">
                        Order #{order.orderNumberFormatted}
                    </h1>
                    <p className="text-neutral mt-1">
                        Total ({itemCount} {itemCount === 1 ? "Item" : "Items"}
                        ): {currencyFormatter.format(orderTotal)}
                    </p>
                    <p className="text-sm text-neutral/60 mt-1">
                        Order date: {formatOrderDate(order.createdAt)}
                    </p>
                    {/* #214 (confirmation half): Download Invoice under the header.
                        Nothing — not even the spacing wrapper — until the order
                        carries an invoice: null means none was issued yet (an
                        ACH order's invoice follows settlement and arrives by
                        email, so none-yet is not an escalation here). */}
                    {order.invoice && (
                        <div
                            data-testid="invoice-download"
                            className="mt-3 flex items-center justify-center gap-2"
                        >
                            <InvoiceDownload
                                invoice={order.invoice}
                                variant="customer"
                            />
                            <InvoiceVoidedBadge invoice={order.invoice} />
                        </div>
                    )}
                </div>

                <div className="bg-base-100 rounded-lg p-4 md:p-5">
                    <p className="flex items-center gap-2 font-bold text-neutral">
                        <Icon
                            name="check_circle"
                            className="text-success text-xl"
                        />
                        Your order is confirmed.
                    </p>
                    <p className="text-sm text-neutral/70 mt-1">
                        Thank you for your purchase. A confirmation has been
                        sent to {order.userEmail}.
                    </p>
                </div>

                {/* §1.11: an ACH order holds until the bank settles — say so, and ONLY
                    then (any other/absent status renders nothing). Explicit
                    text-info-content for the same theme-quirk reason as checkout's
                    error alert. */}
                {order.paymentStatus ===
                    OrderPaymentStatusType.PendingSettlement && (
                    <div
                        role="status"
                        className="alert alert-info text-info-content"
                    >
                        <Icon name="schedule" filled />
                        <span>{PENDING_SETTLEMENT_NOTICE}</span>
                    </div>
                )}

                {/* A failed payment says so; no link to the Payment Returned
                    page (a returned order stays returned after the hold lifts).
                    Explicit text-error-content for the same theme-quirk reason
                    as the notice above. */}
                {failureNote && (
                    <div
                        role="status"
                        className="alert alert-error text-error-content"
                    >
                        <Icon name="error" filled />
                        <span>{failureNote}</span>
                    </div>
                )}

                <ShippingCard location={shippingLocation} />

                <div className="bg-base-100 rounded-lg p-4 md:p-5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-neutral/60">
                        Payment
                    </p>
                    <p className="font-semibold text-neutral mt-1">
                        {order.paymentMethodBrand && order.paymentMethodLast4
                            ? `${order.paymentMethodBrand} ••••${order.paymentMethodLast4}`
                            : "•••• ••••"}
                    </p>
                </div>

                <section className="bg-base-100 rounded-lg p-4 md:p-5">
                    <h2 className="font-bold text-lg text-neutral mb-2">
                        Your Items ({products.length})
                    </h2>
                    <div>
                        {products.map((product) => (
                            <ConfirmationItemRow
                                // The cart is line-per-offering: the same
                                // product from two vendors is two lines, so
                                // productId alone is not unique here.
                                key={`${product.productId}-${product.skuId}`}
                                product={product}
                            />
                        ))}
                    </div>

                    <div className="mt-4 pt-3 border-t border-base-300 flex flex-col gap-1">
                        <div className="flex items-baseline justify-between gap-3">
                            <span className="text-sm text-neutral/70">
                                {itemCount}{" "}
                                {itemCount === 1 ? "Item" : "Items"}
                            </span>
                            <span className="text-sm font-semibold text-neutral">
                                {currencyFormatter.format(subtotal)}
                            </span>
                        </div>
                        <div className="flex items-baseline justify-between gap-3">
                            <span className="text-sm text-neutral/70">
                                Shipping &amp; handling
                            </span>
                            <span className="text-sm font-semibold text-neutral">
                                {currencyFormatter.format(shippingAndHandling)}
                            </span>
                        </div>
                        {surchargeAmount > 0 && (
                            <div className="flex items-baseline justify-between gap-3">
                                <span className="text-sm text-neutral/70">
                                    Card processing fee
                                </span>
                                <span className="text-sm font-semibold text-neutral">
                                    {currencyFormatter.format(surchargeAmount)}
                                </span>
                            </div>
                        )}
                        <div className="flex items-baseline justify-between gap-3 border-t border-base-300 pt-2 mt-1">
                            <span className="text-base font-bold text-neutral">
                                Order total
                            </span>
                            <span className="text-lg font-bold text-neutral">
                                {currencyFormatter.format(orderTotal)}
                            </span>
                        </div>
                    </div>
                </section>

                <Link
                    to={ROUTES.BROWSE}
                    className="btn btn-warning w-full text-neutral font-bold"
                >
                    Continue Shopping
                </Link>
            </div>
        </div>
    );
};
